import { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import Head from 'next/head';

import { StrapiService } from 'lib/strapi/StrapiService';
import { StrapiArticle } from 'types/strapiService/types';
import { Article as ArticleView } from 'components/views/Article';
import { getStrapiImageUrl } from 'lib/strapi/getStrapiImageUrl';

interface Props {
  article: StrapiArticle;
}

const Article: NextPage<Props> = ({ article }) => {
  const router = useRouter();
  const canonicalUrl = `https://${process.env.NEXT_PUBLIC_SITE_DOMAIN}${router.asPath}`;

  return (
    <>
      <Head>
        <title>{article.attributes.seo.metaTitle} - Blipp</title>
        <meta
          property="og:title"
          content={`${article.attributes.seo.metaTitle} - Blipp`}
          key="og-title"
        />
        {article.attributes.seo?.metaDescription && (
          <>
            <meta name="description" content={article.attributes.seo.metaDescription} />
            <meta property="og:description" content={article.attributes.seo.metaDescription} />
          </>
        )}
        {article.attributes.seo?.shareImage.data && (
          <>
            <meta
              property="og:image"
              content={getStrapiImageUrl(article.attributes.seo.shareImage.data.attributes.url)}
              key="og-image"
            />
            <meta
              name="image"
              content={getStrapiImageUrl(article.attributes.seo.shareImage.data.attributes.url)}
              key="image"
            />
            <meta
              property="og:image:type"
              content={article.attributes.seo.shareImage.data.attributes.mime}
              key="og-image-type"
            />
            <meta
              property="og:image:alt"
              content={article.attributes.seo.shareImage.data.attributes.alternativeText}
              key="og-image-alt"
            />
          </>
        )}
        <meta property="og:type" content="article" />

        <link rel="canonical" key="canonical" href={canonicalUrl} />
      </Head>

      <ArticleView article={article} />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  if (!query?.article) {
    return {
      notFound: true,
    };
  }

  const [articles, mostPopularArticles] = await Promise.all([
    // @ts-ignore
    StrapiService.getArticle(query?.article),
    StrapiService.getArticlesByMostPopular(),
  ]);

  // @ts-ignore
  if (!articles) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      // @ts-ignore
      article: articles,
      mostPopularArticles,
    },
  };
};

export default Article;
